import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CultureSection = () => {
  const cultureValues = [
    {
      id: 1,
      title: "Continuous Learning",
      description: "We invest 20% of our time in learning new technologies, attending conferences, and exploring emerging trends to stay ahead of the curve.",
      icon: "BookOpen",
      color: "primary",
      stats: "40+ hours/month per team member",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
    },
    {
      id: 2,
      title: "Innovation Lab",
      description: "Our experimental projects division explores AI integration, emerging technologies, and creative solutions that push the boundaries of what's possible.",
      icon: "Lightbulb",
      color: "accent",
      stats: "5+ experimental projects annually",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
    },
    {
      id: 3,
      title: "Work-Life Balance",
      description: "We believe that creativity flourishes when our team is happy and balanced. Flexible hours, remote work options, and wellness programs support our people.",
      icon: "Heart",
      color: "success",
      stats: "4.8/5 employee satisfaction",
      image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
    },
    {
      id: 4,
      title: "Collaborative Spirit",
      description: "Every voice matters in our team. We foster an environment where ideas flow freely, feedback is welcomed, and everyone contributes to our collective success.",
      icon: "Users",
      color: "trust",
      stats: "100% team participation in decisions",
      image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
    }
  ];

  const experimentalProjects = [
    {
      id: 1,
      title: "AI-Powered Design Assistant",
      description: "Developing an AI tool that helps designers create more effective layouts based on user behavior patterns and conversion data.",
      status: "In Development",
      technology: "Machine Learning, React",
      impact: "30% faster design iterations",
      icon: "Bot"
    },
    {
      id: 2,
      title: "Voice Commerce Platform",
      description: "Exploring voice-activated e-commerce experiences that allow users to shop using natural language commands.",
      status: "Prototype",
      technology: "Voice Recognition, Node.js",
      impact: "Accessibility improvement",
      icon: "Mic"
    },
    {
      id: 3,
      title: "AR Product Visualization",
      description: "Creating augmented reality solutions that let customers visualize products in their own space before purchasing.",
      status: "Beta Testing",
      technology: "WebAR, Three.js",
      impact: "40% reduction in returns",
      icon: "Eye"
    },
    {
      id: 4,
      title: "Blockchain Authentication",
      description: "Implementing decentralized identity verification for enhanced security and user privacy in web applications.",
      status: "Research Phase",
      technology: "Blockchain, Web3",
      impact: "Enhanced security",
      icon: "Shield"
    }
  ];

  const teamActivities = [
    {
      title: "Monthly Tech Talks",
      description: "Team members share knowledge about new technologies and industry trends",
      frequency: "Monthly",
      icon: "Presentation"
    },
    {
      title: "Hackathon Fridays",
      description: "Dedicated time for experimental projects and creative problem-solving",
      frequency: "Bi-weekly",
      icon: "Code"
    },
    {
      title: "Client Success Celebrations",
      description: "Celebrating project milestones and client achievements together",
      frequency: "Per Project",
      icon: "PartyPopper"
    },
    {
      title: "Skill Development Workshops",
      description: "External trainers and internal experts conduct skill-building sessions",
      frequency: "Quarterly",
      icon: "GraduationCap"
    }
  ];

  const benefits = [
    { icon: "Clock", title: "Flexible Hours", description: "Work when you\'re most productive" },
    { icon: "Home", title: "Remote Work", description: "Work from anywhere options" },
    { icon: "Heart", title: "Health & Wellness", description: "Comprehensive health coverage" },
    { icon: "BookOpen", title: "Learning Budget", description: "₹2000 annual learning allowance" },
    { icon: "Plane", title: "Conference Attendance", description: "Sponsored industry events" },
    { icon: "Coffee", title: "Team Retreats", description: "Quarterly team building activities" }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-trust rounded-lg flex items-center justify-center">
              <Icon name="Sparkles" size={24} className="text-white" />
            </div>
            <span className="text-trust font-semibold text-lg">Company Culture</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Innovation Through People
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Our culture of continuous learning, innovation, and collaboration creates an environment where creativity thrives and breakthrough solutions emerge.
          </p>
        </div>

        {/* Culture Values */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {cultureValues?.map((value) => (
            <div key={value?.id} className="bg-white rounded-2xl p-8 brand-shadow brand-transition brand-hover-lift">
              <div className="flex items-start space-x-6">
                <div className="relative">
                  <Image
                    src={value?.image}
                    alt={`${value?.title} culture illustration`}
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                  <div className={`absolute -bottom-2 -right-2 w-10 h-10 bg-${value?.color} rounded-lg flex items-center justify-center`}>
                    <Icon name={value?.icon} size={20} className="text-white" />
                  </div>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-foreground mb-3">{value?.title}</h3>
                  <p className="text-muted-foreground mb-4">{value?.description}</p>
                  <div className="flex items-center space-x-2">
                    <Icon name="TrendingUp" size={16} className="text-accent" />
                    <span className="text-sm font-semibold text-accent">{value?.stats}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Experimental Projects */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-4">Innovation Lab Projects</h3>
            <p className="text-lg text-muted-foreground">
              Exploring tomorrow's technologies today through experimental projects and R&D initiatives
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {experimentalProjects?.map((project) => (
              <div key={project?.id} className="bg-white rounded-2xl p-6 brand-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Icon name={project?.icon} size={20} className="text-accent" />
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground">{project?.title}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        project?.status === 'In Development' ? 'bg-warning/10 text-warning' :
                        project?.status === 'Beta Testing' ? 'bg-success/10 text-success' :
                        project?.status === 'Prototype'? 'bg-trust/10 text-trust' : 'bg-muted text-muted-foreground'
                      }`}>
                        {project?.status}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-muted-foreground mb-4">{project?.description}</p>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Technology:</span>
                    <span className="font-medium text-foreground">{project?.technology}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Expected Impact:</span>
                    <span className="font-medium text-success">{project?.impact}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Team Activities */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-4">Team Activities & Growth</h3>
            <p className="text-lg text-muted-foreground">
              Regular activities that foster learning, collaboration, and team bonding
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamActivities?.map((activity, index) => (
              <div key={index} className="bg-white rounded-xl p-6 brand-shadow text-center brand-transition brand-hover-lift">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name={activity?.icon} size={24} className="text-primary" />
                </div>
                <h4 className="font-bold text-foreground mb-2">{activity?.title}</h4>
                <p className="text-sm text-muted-foreground mb-3">{activity?.description}</p>
                <span className="text-xs px-2 py-1 bg-accent/10 text-accent rounded-full">{activity?.frequency}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits & Perks */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-4">Benefits & Perks</h3>
            <p className="text-lg text-muted-foreground">
              We invest in our team's well-being, growth, and happiness
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits?.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 brand-shadow">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name={benefit?.icon} size={20} className="text-success" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">{benefit?.title}</h4>
                    <p className="text-sm text-muted-foreground">{benefit?.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Culture Impact */}
        <div className="bg-gradient-to-r from-trust to-primary rounded-2xl p-8 text-white">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">Culture Creates Impact</h3>
              <p className="text-white/90 mb-6">
                Our investment in people and culture directly translates to better outcomes for our clients and innovative solutions that push industry boundaries.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-accent" />
                  <span>Zero employee turnover in 2023</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-accent" />
                  <span>5+ patents filed for innovative solutions</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-accent" />
                  <span>15+ industry conference presentations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-accent" />
                  <span>100% client satisfaction with team collaboration</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <Image
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                alt="Singh Creations team celebrating success and innovation"
                className="w-full h-64 object-cover rounded-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CultureSection;